import React, { useContext } from 'react';
import styled from 'astroturf/react';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import {
  getImpressionFeedExtraData,
  getImpressionPaperType,
  getSocialImpressionSource,
} from 'js/social/ImpressionHelper';
import { FeedContext } from '../contexts/feed';

import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import routes from 'sitewide-js/core_pre_webpack/routes';
import { FollowableUser, NewsType, PaperType } from 'js/social/shared/types';
import AvatarBase, { Name } from 'js/social/shared/Avatar';
import { getAge } from 'js/social/shared/dateUtils';
import _Container from "Header-Container.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=Container";
import _Avatar from "Header-Avatar.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=Avatar";
import _Author from "Header-Author.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=Author";
import _AuthorDetails from "Header-AuthorDetails.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=AuthorDetails";
import _AuthorTitle from "Header-AuthorTitle.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=AuthorTitle";
import _AuthorName from "Header-AuthorName.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=AuthorName";
import _AuthorAdditional from "Header-AuthorAdditional.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=AuthorAdditional";
import _AuthorAdditionalLink from "Header-AuthorAdditionalLink.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=AuthorAdditionalLink";
import _Cta from "Header-Cta.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/News/Header.tsx?styleId=Cta";


const Container = /*#__PURE__*/styled("div", null, {
  displayName: "Container",
  styles: _Container
});

const Avatar = /*#__PURE__*/styled(AvatarBase, null, {
  displayName: "Avatar",
  styles: _Avatar
});

const Author = /*#__PURE__*/styled("div", null, {
  displayName: "Author",
  styles: _Author
});

const AuthorDetails = /*#__PURE__*/styled("div", null, {
  displayName: "AuthorDetails",
  styles: _AuthorDetails
});

const AuthorTitle = /*#__PURE__*/styled(ImpressionWatcher, null, {
  displayName: "AuthorTitle",
  styles: _AuthorTitle
});

const AuthorName = /*#__PURE__*/styled(Name, null, {
  displayName: "AuthorName",
  styles: _AuthorName
});

const AuthorAdditional = /*#__PURE__*/styled(Body, null, {
  displayName: "AuthorAdditional",
  styles: _AuthorAdditional
});

const AuthorAdditionalLink = /*#__PURE__*/styled(BodyLink, null, {
  displayName: "AuthorAdditionalLink",
  styles: _AuthorAdditionalLink
});

const Cta = /*#__PURE__*/styled("div", null, {
  displayName: "Cta",
  styles: _Cta
});

const getPaperUpdateSentence = (paperType: PaperType | null): string | null => {
  switch (paperType) {
    case 'certified_work':
      return 'Recommended';
    case 'paper_upload':
      return 'Uploaded';
    case 'bookmark':
      return `Bookmarked`;
    default:
      return null;
  }
};

interface Props {
  className?: string;
  user: FollowableUser;
  userUrl?: string;
  createdAt: string;
  newsType: NewsType;
  newsId: number | string;
  paperType: PaperType | null;
  cta?: React.ReactNode;
  isInQuotedNews?: boolean;
  feedIndex?: number;
  feedReason?: string;
  recommendationScore?: number;
}

export const Header: React.FunctionComponent<Props> = ({
  className,
  user,
  userUrl,
  createdAt,
  newsType,
  newsId,
  paperType,
  cta,
  isInQuotedNews = false,
  feedIndex,
  feedReason,
  recommendationScore,
}) => {
  const paperUpdateSentence = getPaperUpdateSentence(paperType);
  const {
    feedConfig: { page },
  } = useContext(FeedContext);
  const trackImpressions = page === 'home' && !isInQuotedNews;
  const IMPRESSION_SECTION =
    typeof feedIndex === 'number' ? `feed-${feedIndex}` : '';
  const IMPRESSION_PAPER_TYPE = getImpressionPaperType(paperType);
  const IMPRESSION_EXTRA_DATA = getImpressionFeedExtraData({
    newsType,
    newsId,
    feedIndex,
    feedReason,
    recommendationScore,
  });

  return (
    <Container className={className}>
      <Author>
        <ImpressionWatcher
          active={trackImpressions}
          source={getSocialImpressionSource(
            page,
            IMPRESSION_SECTION,
            'prof-p',
            IMPRESSION_PAPER_TYPE,
          )}
          entity_type={ImpressionEntity.User}
          entity_id={user.id}
          extra={IMPRESSION_EXTRA_DATA}
        >
          <Avatar
            className={'clickable'}
            fullName={user.displayName}
            photoUrl={user.photo}
            profileUrl={userUrl}
            small={isInQuotedNews}
          />
        </ImpressionWatcher>
        <AuthorDetails>
          <AuthorTitle
            active={trackImpressions}
            source={getSocialImpressionSource(
              page,
              IMPRESSION_SECTION,
              'prof-n',
            )}
            entity_type={ImpressionEntity.User}
            entity_id={user.id}
            extra={IMPRESSION_EXTRA_DATA}
          >
            <AuthorName href={userUrl} className="clickable">
              {user.displayName}
            </AuthorName>
          </AuthorTitle>
          {user.university != null && (
            <AuthorAdditional size="xs">
              {user.university.displayName}
            </AuthorAdditional>
          )}
          {!isInQuotedNews && (
            <AuthorAdditional size="xs">
              <AuthorAdditionalLink
                size="xs"
                href={
                  newsType === 'post'
                    ? routes.community_show_post_url({ post_id: newsId })
                    : routes.community_show_paper_update_url({
                        paper_update_id: newsId,
                      })
                }
              >
                {getAge(createdAt)}
              </AuthorAdditionalLink>
              {paperUpdateSentence && <span>{paperUpdateSentence}</span>}
            </AuthorAdditional>
          )}
        </AuthorDetails>
      </Author>
      {cta && !isInQuotedNews && <Cta>{cta}</Cta>}
    </Container>
  );
};
